import { useRef } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BiSolidBot,
  BiSolidChat,
  BiSolidImageAdd,
  BiSolidMessageDetail,
  BiSolidTrash,
  BiSolidVideoPlus
} from "react-icons/bi";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import PopoverWithAudio, {
  IPopoverWithAudioType
} from "../../components/common/PopoverWithAudio";
import ProBadge from "../../components/ui/ProBadge";
import { AlertsType } from "../../constants/alerts.constant";
import usePermission from "../../hooks/usePermission";
import Utilities from "../../utils";
import AppearanceCustomPoweredBy from "./AppearanceCustomPoweredBy";
import AppearanceMessageNotificationComponent from "./AppearanceMessageNotification.component";
import AppearanceNotificationComponent from "./AppearanceNotification.component";
import { BiLouise } from "./AppearanceSharedAi";
import {
  handleResetAppearance,
  handleUpdateAppearanceItem,
  selectAppearanceState
} from "./appearanceSlice";
import ColorDropDown from "./ColorDropDown";
import { IAiAppearance, IVideoScenarioAppearance } from "./types";
import AppearanceMessageSound from "./AppearanceMessageSound.component";

const AppearanceVideoScenario = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();

  const permissions = usePermission();
  const { configs, originalData } = useAppSelector(selectAppearanceState);

  const iconImageRef = useRef<HTMLInputElement | null>(null);
  const iconVideoRef = useRef<HTMLInputElement | null>(null);

  const voiceResponsePopData: IPopoverWithAudioType = {
    audioUrl: Utilities.VoiceResponseAudioLinks[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-10")
  };

  const SaveStatsData: IPopoverWithAudioType = {
    audioUrl: Utilities.VoiceSaveStatsTrack[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-09")
  };

  const {
    register,
    setValue,
    watch,
    formState: { errors },
    control
  } = useFormContext<IAiAppearance>();

  // ========================= General ============================ //
  const handleUpdateItem = (key: keyof IAiAppearance, value: any) => {
    if (
      key === "icon" &&
      value.type === "image" &&
      value.value &&
      typeof value.value === "object"
    ) {
      const file: File = value.value;

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return;
      }

      if (file.size / (1024 * 1024) > 5) {
        toast.error(t(AlertsType.ISSTKO008, { size: 5 }));
        return;
      }

      if (
        !["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(
          file.type
        )
      ) {
        toast.error(t(AlertsType.ISSTKO009));
        return;
      }
    }

    if (
      key === "icon" &&
      value.type === "video" &&
      value.value &&
      typeof value.value === "object"
    ) {
      const file: File = value.value;

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return;
      }

      if (file.size / (1024 * 1024) > 30) {
        toast.error(t(AlertsType.ISSTKO008, { size: 30 }));
        return;
      }

      if (file?.type) {
        if (!Utilities.isSupportedVideoFormatMimeType(file.type)) {
          toast.error(t(AlertsType.ISSTKO009));
          return;
        }
      } else if (file?.name) {
        if (!Utilities.isSupportedVideoFormatWithFileName(file.type)) {
          toast.error(t(AlertsType.ISSTKO009));
          return;
        }
      }
    }

    setValue(key, value);
    dispatch(handleUpdateAppearanceItem({ key, value }));

    if (key === "icon" && value.type !== "video")
      dispatch(
        handleUpdateAppearanceItem({
          key: "roundedVideo",
          value: (configs as IVideoScenarioAppearance).roundedVideo
        })
      );
  };

  const alwaysShowButtonsPopData: IPopoverWithAudioType = {
    audioUrl: Utilities.alwaysShowButtonsAudioLinks[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-11")
  };

  return (
    <div className="appearance-video-scenario tw-w-full">
      {/* Theme */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t("DashboardSettingCustomizeChat/l-06")}
        </div>
        <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
          <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-w-full">
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("DashboardSettingCustomizeChat/l-12")}
                  {!permissions.settingsPage.widget.customization.theme && (
                    <ProBadge className="tw-ms-2" />
                  )}
                </Form.Label>
                <Form.Select
                  disabled={
                    !permissions.settingsPage.widget.customization.theme
                  }
                  isInvalid={!!errors.theme}
                  {...register("theme", {
                    onChange(event) {
                      dispatch(
                        handleUpdateAppearanceItem({
                          key: "theme",
                          value: event.target.value
                        })
                      );
                    }
                  })}
                >
                  <option value={"light"}>
                    {t("DashboardSettingCustomizeChat/l-05")}
                  </option>
                  <option value={"dark"}>
                    {t("DashboardSettingCustomizeChat/l-07")}
                  </option>
                </Form.Select>
                {errors.theme && (
                  <span className="tw-text-red-500 tw-text-sm tw-block">
                    {errors.theme?.message}
                  </span>
                )}
              </Form.Group>
            </div>
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("DashboardSettingCustomizeChat/t-01")}
                  {!permissions.settingsPage.widget.customization.colors && (
                    <ProBadge className="tw-ms-2" />
                  )}
                </Form.Label>
                <Controller
                  control={control}
                  name="color"
                  render={({ field: { onChange, value } }) => (
                    <ColorDropDown
                      acceptableColors={[
                        { label: "Grey", value: "#6B7280" },
                        { label: "Red", value: "#EF4444" },
                        { label: "Orange", value: "#F97316" },
                        { label: "Yellow", value: "#F59E0B" },
                        { label: "Green", value: "#10B981" },
                        { label: "Teal", value: "#14B8A6" },
                        { label: "Cyan", value: "#06B6D4" },
                        { label: "Light Blue", value: "#0EA5E9" },
                        { label: "Blue", value: "#3B82F6" },
                        { label: "Indigo", value: "#6366F1" },
                        { label: "Purple (Violet)", value: "#8B5CF6" },
                        { label: "Pink", value: "#EC4899" }
                      ]}
                      disabled={
                        !permissions.settingsPage.widget.customization.colors
                      }
                      isInvalid={!!errors.color}
                      errorMessage={errors.color?.message}
                      onColorChanged={(color) => {
                        onChange(color);

                        dispatch(
                          handleUpdateAppearanceItem({
                            key: "color",
                            value: color
                          })
                        );
                      }}
                      currentColor={value}
                    />
                  )}
                />
              </Form.Group>
            </div>
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("DashboardSettingCustomizeChat/t-02")}
                  {!permissions.settingsPage.widget.customization.position && (
                    <ProBadge className="tw-ms-2" />
                  )}
                </Form.Label>
                <Form.Select
                  disabled={
                    !permissions.settingsPage.widget.customization.position
                  }
                  isInvalid={!!errors.position}
                  {...register("position", {
                    onChange(event) {
                      dispatch(
                        handleUpdateAppearanceItem({
                          key: "position",
                          value: event.target.value
                        })
                      );
                    }
                  })}
                >
                  <option value={"right"}>
                    {t("DashboardSettingCustomizeChat/l-09")}
                  </option>
                  <option value={"left"}>
                    {t("DashboardSettingCustomizeChat/l-08")}
                  </option>
                </Form.Select>
                {errors.position && (
                  <span className="tw-text-red-500 tw-text-sm tw-block">
                    {errors.position?.message}
                  </span>
                )}
              </Form.Group>
            </div>
          </div>
        </div>
      </div>
      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

      {/* Floating button */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t("DashboardSettingCustomizeChat/floating-btn")}
        </div>
        <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
          <Form.Group className="tw-mb-4">
            <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              {t("DashboardSettingCustomizeChat/t-03")}
              {!permissions.settingsPage.widget.customization.icon && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
            </Form.Label>
            <div className="tw-flex tw-items-center tw-gap-5 tw-flex-wrap">
              {[
                { id: 4, ui: <BiLouise /> },
                { id: 1, ui: <BiSolidBot size={25} /> },
                { id: 2, ui: <BiSolidChat size={25} /> },
                { id: 3, ui: <BiSolidMessageDetail size={25} /> }
              ].map((item) => (
                <button
                  type="button"
                  disabled={!permissions.settingsPage.widget.customization.icon}
                  key={item.id}
                  className={`tw-border-0 tw-w-10 tw-h-10 ${
                    (configs as IAiAppearance).icon.type === "icon" &&
                    item.id === Number((configs as IAiAppearance).icon.value)
                      ? "tw-bg-blue-500"
                      : "tw-bg-gray-400"
                  } tw-text-white tw-rounded-[999px] tw-flex tw-items-center tw-flex-col tw-justify-center`}
                  onClick={() =>
                    handleUpdateItem("icon", {
                      type: "icon",
                      value: item.id.toString()
                    })
                  }
                >
                  {item.ui}
                </button>
              ))}

              {/* Image manager */}
              <button
                type="button"
                disabled={!permissions.settingsPage.widget.customization.icon}
                className={`tw-pl-3 tw-pr-4 tw-py-2  ${
                  (configs as IAiAppearance).icon.type === "image"
                    ? "tw-bg-blue-500 tw-text-white"
                    : "tw-bg-white tw-text-gray-400"
                } tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal`}
                onClick={() =>
                  handleUpdateItem("icon", {
                    type: "image",
                    value:
                      (originalData as IAiAppearance).icon.type === "image" &&
                      (originalData as IAiAppearance).icon.value
                        ? (originalData as IAiAppearance).icon.value
                        : ""
                  })
                }
              >
                <BiSolidImageAdd size={22} className=" tw-mr-2" />
                Image
              </button>

              {/* Video manage */}
              <button
                type="button"
                disabled={!permissions.settingsPage.widget.customization.icon}
                className={`tw-pl-3 tw-pr-4 tw-py-2 ${
                  (configs as IAiAppearance).icon.type === "video"
                    ? "tw-bg-blue-500 tw-text-white"
                    : "tw-bg-white tw-text-gray-400"
                } tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal`}
                onClick={() =>
                  handleUpdateItem("icon", {
                    type: "video",
                    value:
                      (originalData as IAiAppearance).icon.type === "video" &&
                      (originalData as IAiAppearance).icon.value
                        ? (originalData as IAiAppearance).icon.value
                        : ""
                  })
                }
              >
                <BiSolidVideoPlus size={22} className="tw-mr-2" />
                Video
              </button>
            </div>
          </Form.Group>
        </div>

        {/* Image manager */}
        {(configs as IAiAppearance).icon.type === "image" && (
          <Form.Group className="lg:tw-w-[70%] tw-w-full tw-mb-4">
            <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              {t("DashboardSettingCustomizeChat/upload-image")}
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="file"
                accept="image/png, image/jpeg, image/jpg, image/gif"
                isInvalid={
                  !!iconImageRef.current?.files &&
                  !(configs as IAiAppearance).icon.value
                }
                ref={iconImageRef}
                onChange={(e: any) =>
                  handleUpdateItem("icon", {
                    type: "image",
                    value: e.target.files[0]
                  })
                }
              />
              <InputGroup.Text>
                <BiSolidTrash
                  size={22}
                  className="tw-text-gray-400"
                  onClick={() => {
                    if (iconImageRef.current) {
                      iconImageRef.current.value = "";

                      handleUpdateItem("icon", {
                        type: "image",
                        value: null
                      });
                    }
                  }}
                />
              </InputGroup.Text>
            </InputGroup>
            {(configs as IAiAppearance).icon.value &&
              typeof (configs as IAiAppearance).icon.value !== "object" && (
                <div className="tw-mt-6">
                  <span className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-3">
                    Current image:
                  </span>{" "}
                  <a
                    href={(configs as IAiAppearance).icon.value}
                    target="_blank"
                    rel="noreferrer"
                    className="tw-text-sm tw-mt-6 tw-text-blue-400 tw-underline"
                  >
                    Link
                  </a>
                </div>
              )}
          </Form.Group>
        )}

        {/* Video manage */}
        {(configs as IAiAppearance).icon.type === "video" && (
          <Form.Group className="lg:tw-w-[70%] tw-w-full tw-mb-4">
            <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              {t("DashboardSettingCustomizeChat/upload-video")}
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="file"
                accept={"video/*"}
                isInvalid={
                  !!iconVideoRef.current?.files &&
                  !(configs as IAiAppearance).icon.value
                }
                ref={iconVideoRef}
                onChange={(e: any) =>
                  handleUpdateItem("icon", {
                    type: "video",
                    value: e.target.files[0]
                  })
                }
              />
              <InputGroup.Text>
                <BiSolidTrash
                  size={22}
                  className="tw-text-gray-400"
                  onClick={() => {
                    if (iconVideoRef.current) {
                      iconVideoRef.current.value = "";

                      handleUpdateItem("icon", {
                        type: "video",
                        value: ""
                      });
                    }
                  }}
                />
              </InputGroup.Text>
            </InputGroup>
            {(configs as IAiAppearance).icon.value &&
              typeof (configs as IAiAppearance).icon.value !== "object" && (
                <div className="tw-mt-6">
                  <span className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-3">
                    Current video:
                  </span>{" "}
                  <a
                    href={(configs as IAiAppearance).icon.value}
                    target="_blank"
                    rel="noreferrer"
                    className="tw-text-sm tw-mt-6 tw-text-blue-400 tw-underline"
                  >
                    Link
                  </a>
                </div>
              )}
          </Form.Group>
        )}

        {/* Video shape */}
        {(configs as IAiAppearance).icon.type === "video" && (
          <Form.Group className="tw-mb-4">
            <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              {t("DashboardSettingCustomizeChat/button-shape")}
              {!permissions.settingsPage.widget.customization.roundedVideo && (
                <ProBadge className="tw-ms-2" />
              )}
            </Form.Label>
            <div className="tw-flex tw-items-center tw-gap-6">
              <button
                type="button"
                onClick={() => handleUpdateItem("roundedVideo", false)}
                className={`${
                  !(configs as IVideoScenarioAppearance).roundedVideo
                    ? "tw-border-4 tw-border-solid tw-border-blue-500"
                    : "tw-border-4 tw-border-solid tw-border-transparent"
                } tw-w-9 tw-h-10 tw-bg-gray-300 tw-rounded ${
                  (configs as IVideoScenarioAppearance).icon.type !== "video"
                    ? "tw-cursor-not-allowed"
                    : ""
                }`}
              />
              <button
                type="button"
                onClick={() => handleUpdateItem("roundedVideo", true)}
                className={`${
                  (configs as IVideoScenarioAppearance).roundedVideo
                    ? "tw-border-4 tw-border-solid tw-border-blue-500"
                    : "tw-border-4 tw-border-solid tw-border-transparent"
                } tw-w-10 tw-h-10 tw-bg-gray-300 tw-rounded-full`}
              />
            </div>
          </Form.Group>
        )}

        {/* <Form.Group className="tw-mb-4">
          <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            Message
          </Form.Label>
          <Form.Control
            placeholder="Hello"
            {...register("notificationMessage", {
              onChange: (e) => {
                dispatch(
                  handleUpdateAppearanceItem({
                    key: "notificationMessage",
                    value: e.target.value
                  })
                );
              }
            })}
          />
        </Form.Group> */}

        {errors.icon && (
          <span className="tw-text-red-500 tw-text-sm tw-block">
            {errors.icon?.message}
          </span>
        )}
      </div>

      <AppearanceNotificationComponent
        register={register}
        watch={watch}
        className="tw-mt-6"
      />

      <AppearanceMessageNotificationComponent
        register={register}
        watch={watch}
        errors={errors}
        control={control}
      />

      <AppearanceMessageSound
        register={register}
        watch={watch}
        errors={errors}
        control={control}
      />

      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

      {/* Other */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t("DashboardSettingCustomizeChat/other")}
        </div>
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
          <Form.Group className="tw-flex tw-items-center tw-gap-2">
            <Form.Check
              disabled={
                !permissions.settingsPage.widget.customization.alwaysShowButtons
              }
              type="switch"
              id="alwaysShowButtons"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t("DashboardSettingCustomizeChat/t-11")}
              isInvalid={!!errors.alwaysShowButtons}
              feedbackType="invalid"
              feedback={errors.alwaysShowButtons?.message}
              {...register("alwaysShowButtons", {
                onChange: (e) => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "alwaysShowButtons",
                      value: e.target.checked
                    })
                  );
                }
              })}
            />
            {!permissions.settingsPage.widget.customization
              .alwaysShowButtons && <ProBadge className="tw-ms-2" />}
            <PopoverWithAudio
              data={{
                contentText: alwaysShowButtonsPopData.contentText,
                audioUrl: alwaysShowButtonsPopData.audioUrl
              }}
            />
          </Form.Group>

          <AppearanceCustomPoweredBy
            hasPermission={
              permissions.settingsPage.widget.customization.poweredBy
            }
            isInvalid={!!errors.customPoweredBy}
            errors={errors.customPoweredBy}
            setFormValue={setValue}
            register={register}
            watch={watch}
          />

          <Form.Group className="tw-flex tw-items-center tw-gap-2">
            <Form.Check
              disabled={
                !permissions.settingsPage.widget.customization.defaultOpen
              }
              type="switch"
              id="isOpen"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t("DashboardSettingCustomizeChat/l-04")}
              isInvalid={!!errors.isOpen}
              feedbackType="invalid"
              feedback={errors.isOpen?.message}
              {...register("isOpen", {
                onChange: (e) => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "isOpen",
                      value: e.target.checked
                    })
                  );
                }
              })}
            />
            {!permissions.settingsPage.widget.customization.defaultOpen && (
              <ProBadge className="tw-ms-2" />
            )}
          </Form.Group>

          {watch("isOpen") && (
            <Form.Group className="tw-flex tw-items-center tw-gap-2">
              <Form.Check
                disabled={
                  !permissions.settingsPage.widget.customization.isFullScreen
                }
                type="switch"
                id="isFullScreen"
                className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                label={t("DashboardSettingCustomizeChat/l-15")}
                isInvalid={!!errors.isFullScreen}
                feedbackType="invalid"
                feedback={errors.isFullScreen?.message}
                {...register("isFullScreen", {
                  onChange: (e) => {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: "isFullScreen",
                        value: e.target.checked
                      })
                    );
                  }
                })}
              />
              {!permissions.settingsPage.widget.customization.isFullScreen && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
            </Form.Group>
          )}

          <Form.Group className="tw-flex tw-items-center tw-gap-2">
            <Form.Check
              type="switch"
              id="saveStats"
              disabled={
                !permissions.settingsPage.widget.customization.saveStats
              }
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t("DashboardSettingCustomizeChat/l-17")}
              isInvalid={!!errors.saveStats}
              feedbackType="invalid"
              feedback={errors.saveStats?.message}
              {...register("saveStats", {
                onChange: (e) => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "saveStats",
                      value: e.target.checked
                    })
                  );
                }
              })}
            />
            {!permissions.settingsPage.widget.customization.saveStats && (
              <ProBadge
                className="tw-ms-2"
                onClose={() => {
                  dispatch(handleResetAppearance());
                }}
              />
            )}
            <PopoverWithAudio
              data={{
                contentText: SaveStatsData.contentText,
                audioUrl: SaveStatsData.audioUrl
              }}
            />
          </Form.Group>
        </div>
      </div>
    </div>
  );
};

export default AppearanceVideoScenario;
